import {
  Box,
  Typography,
  Avatar,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  StyledFormControl,
  StyledValidationText,
} from "Components/Shared/FormStyles";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { getVariantConfig } from "Components/Shared/Notification/utils";
import { NotificationVariant } from "Infrastructure/Api/Api";
import { createLabelId } from "Components/Shared/Inputs/Dropdown";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { ExpandArrowIcon } from "Components/Shared/Icons";
import { Resources, useResource } from "Translations/Resources";

type Props<FormType extends FieldValues> = {
  name: FieldPath<FormType>;
  control: Control<FormType, object>;
  label?: string;
};

const options: Array<NotificationVariant> = [
  NotificationVariant.Announcement,
  NotificationVariant.Completed,
  NotificationVariant.Stardust,
  NotificationVariant.Error,
  NotificationVariant.Critical,
];

export const NotificationTypeSelect = <FormType extends FieldValues>({
  name,
  control,
  label,
}: Props<FormType>) => {
  const theme = useTheme();
  const { t } = useResource();
  const labelId = createLabelId(name);

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { error },
      }) => {
        const selectedOption = options.find((option) => option === value);
        const selectedConfig = selectedOption
          ? getVariantConfig(selectedOption, theme)
          : null;

        return (
          <StyledFormControl>
            {label && (
              <Box sx={{ mb: 1 }}>
                <StyledInputLabel hasError={!!error} id={labelId}>
                  <Typography>{label}</Typography>
                </StyledInputLabel>
              </Box>
            )}
            <FormControl fullWidth>
              <Select
                labelId={labelId}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                ref={ref}
                error={!!error}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || !selectedOption) {
                    return (
                      <Typography color="text.secondary">
                        {t(
                          Resources.Components.Shared.NotificationTypeSelect
                            .SelectNotificationType,
                        )}
                      </Typography>
                    );
                  }

                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: selectedConfig?.iconBackgroundColor,
                          mr: 1,
                          "& svg": {
                            height: "15px",
                            width: "15px",
                          },
                        }}
                      >
                        {selectedConfig?.icon}
                      </Avatar>
                      <Typography>
                        {t(Resources.Notifications.Variants[selectedOption])}
                      </Typography>
                    </Box>
                  );
                }}
                IconComponent={() => (
                  <Box
                    position="absolute"
                    right="16px"
                    sx={{
                      pointerEvents: "none",
                      "& svg": {
                        width: "18px",
                        height: "auto",
                        opacity: "0.5",
                      },
                    }}
                  >
                    <ExpandArrowIcon />
                  </Box>
                )}
                sx={{
                  height: "44px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.grey[300],
                  },
                }}
              >
                {options.map((option) => {
                  const config = getVariantConfig(option, theme);
                  return (
                    <MenuItem key={option} value={option}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: config.iconBackgroundColor,
                            mr: 1,
                            "& svg": {
                              height: "15px",
                              width: "15px",
                            },
                          }}
                        >
                          {config.icon}
                        </Avatar>
                        <Typography>
                          {t(Resources.Notifications.Variants[option])}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <StyledValidationText>{error?.message}</StyledValidationText>
          </StyledFormControl>
        );
      }}
    />
  );
};
